/**
 * dopamiles-header.js
 * Menu panel toggle + search expand + cart badge refresh for dopamiles-header.liquid.
 *
 * Responsibilities:
 *  - Hamburger → opens the phone drawer; X, backdrop and Esc close it
 *  - Focus trap inside the open drawer
 *  - Desktop nav-row dropdowns (<details>): one open at a time; outside
 *    click, focus-out and Esc close them
 *  - Search toggle buttons (one per layout) → expand search overlay, focus input
 *  - Menu ↔ search mutex via 'bnc:menu:open' / 'bnc:search:open' custom events
 *  - Bag button → dispatches 'cart:open' (dopamiles-cart.js handles the rest)
 *
 *  Note: bag-count badge is owned by dopamiles-cart.js (see updateBubbleCount).
 *  This file dispatched a duplicate write in an earlier revision; removed to
 *  eliminate divergence at counts >= 100 ("99+" vs "100").
 */

(function () {
  'use strict';

  // ── Element references ──────────────────────────────────────────────────────

  const header        = document.getElementById('bnc-header');
  const hamburger     = document.getElementById('bnc-hamburger');
  const menuPanel     = document.getElementById('bnc-menu-panel');
  const menuBackdrop  = document.getElementById('bnc-menu-backdrop');
  const menuClose     = document.getElementById('bnc-menu-close');
  const bagBtn        = document.getElementById('bnc-bag-btn');
  // Two toggles share the overlay: right cluster on phones, left on desktop.
  const searchToggles = Array.from(document.querySelectorAll('.bnc-search-toggle'));
  const searchOverlay = document.getElementById('bnc-search-overlay');
  const searchClose   = document.getElementById('bnc-search-overlay-close');
  const searchInput   = document.getElementById('bnc-header-search-input');

  // ── Header height tracker ──────────────────────────────────────────────────
  // The predictive search panel and sticky page elements (PDP gallery, cart
  // summary, …) offset from `--bnc-header-h`. We measure here (rather than
  // hard-code) so it survives logo-image swaps, font-load reflow, the desktop
  // nav row and viewport resizes.

  function updateHeaderHeight() {
    if (!header) return;
    // Use rect.bottom, not offsetHeight. The promo-bar block sits above the
    // (relatively-positioned) header in the same section group, so the visible
    // bottom edge of the sticky cluster = promo height + header height at
    // scroll=0, and just header height once scrolled past the promo. offsetHeight
    // would only measure the header element itself, leaving the panel anchor
    // 43px too high and clipping the first nav item behind the promo bar.
    const h = Math.max(0, header.getBoundingClientRect().bottom);
    if (h > 0) {
      document.documentElement.style.setProperty('--bnc-header-h', h + 'px');
    }
  }

  updateHeaderHeight();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(updateHeaderHeight);
  }
  window.addEventListener('resize', updateHeaderHeight, { passive: true });
  // Promo bar scrolls away on page scroll → header.bottom shrinks. rAF-throttle
  // keeps the var fresh without firing per-pixel work.
  let scrollTick = false;
  window.addEventListener('scroll', () => {
    if (scrollTick) return;
    scrollTick = true;
    requestAnimationFrame(() => {
      updateHeaderHeight();
      scrollTick = false;
    });
  }, { passive: true });
  document.addEventListener('shopify:section:load', updateHeaderHeight);

  // ── Menu panel open / close ─────────────────────────────────────────────────

  function openMenu() {
    if (!menuPanel) return;
    menuPanel.classList.add('is-open');
    menuPanel.setAttribute('aria-hidden', 'false');
    if (menuBackdrop) menuBackdrop.classList.add('is-open');
    if (hamburger) {
      hamburger.setAttribute('aria-expanded', 'true');
      hamburger.setAttribute('aria-label', 'Close menu');
    }
    document.body.style.overflow = 'hidden';
    // dopamiles-search.js listens for this to close its predictive panel.
    document.dispatchEvent(new CustomEvent('bnc:menu:open'));
    // Land keyboard users on the X, the drawer's first control.
    if (menuClose) menuClose.focus();
  }

  function closeMenu() {
    if (!menuPanel) return;
    menuPanel.classList.remove('is-open');
    menuPanel.setAttribute('aria-hidden', 'true');
    if (menuBackdrop) menuBackdrop.classList.remove('is-open');
    if (hamburger) {
      hamburger.setAttribute('aria-expanded', 'false');
      hamburger.setAttribute('aria-label', 'Open menu');
      hamburger.focus();
    }
    document.body.style.overflow = '';
  }

  function isMenuOpen() {
    return menuPanel && menuPanel.classList.contains('is-open');
  }

  // ── Focus trap (menu panel) ─────────────────────────────────────────────────

  function getFocusableEls() {
    if (!menuPanel) return [];
    return Array.from(
      menuPanel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((el) => !el.closest('[aria-hidden="true"]') && !el.closest('[hidden]'));
  }

  function trapFocus(e) {
    if (!isMenuOpen() || e.key !== 'Tab') return;

    const focusable = getFocusableEls();
    if (!focusable.length) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ── Sub-menu disclosure (menu panel) ────────────────────────────────────────
  // Delegated: submenu toggles only exist for menu items that have children.
  // State lives in the DOM (aria-expanded + [hidden]) so the focus trap's
  // [hidden] filter and assistive tech read the same truth the eye sees.

  if (menuPanel) {
    menuPanel.addEventListener('click', (e) => {
      const toggle = e.target.closest('.bnc-menu-sub-toggle');
      if (!toggle) return;
      const sub = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!sub) return;
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isOpen));
      sub.hidden = isOpen;
    });
  }

  if (menuClose) menuClose.addEventListener('click', closeMenu);
  if (menuBackdrop) menuBackdrop.addEventListener('click', closeMenu);

  // The drawer is phone-only (CSS hides it from 990px). Close it when the
  // viewport crosses into desktop so the body scroll lock doesn't linger.
  const desktopMq = window.matchMedia('(min-width: 990px)');
  const onDesktopChange = (e) => { if (e.matches && isMenuOpen()) closeMenu(); };
  if (desktopMq.addEventListener) desktopMq.addEventListener('change', onDesktopChange);
  else if (desktopMq.addListener) desktopMq.addListener(onDesktopChange);

  // ── Desktop nav-row dropdowns ───────────────────────────────────────────────
  // Native <details> handles click/Enter/Space. We add: one open at a time,
  // and close on outside click, focus leaving the item, or Esc.

  const navDropdowns = Array.from(header ? header.querySelectorAll('.bnc-nav-dd') : []);

  navDropdowns.forEach((dd) => {
    dd.addEventListener('toggle', () => {
      if (!dd.open) return;
      navDropdowns.forEach((other) => { if (other !== dd) other.open = false; });
    });
    dd.addEventListener('focusout', (e) => {
      if (dd.open && e.relatedTarget && !dd.contains(e.relatedTarget)) dd.open = false;
    });
  });

  function openDropdown() {
    return navDropdowns.find((dd) => dd.open) || null;
  }

  document.addEventListener('click', (e) => {
    navDropdowns.forEach((dd) => { if (dd.open && !dd.contains(e.target)) dd.open = false; });
  });

  // ── Search overlay ──────────────────────────────────────────────────────────
  // Toggles a full-width overlay panel that sits absolute over the header bar.
  // Focus returns to whichever toggle opened it.

  let lastSearchToggle = null;

  function setSearchExpanded(isOpen) {
    searchToggles.forEach((btn) => btn.setAttribute('aria-expanded', String(isOpen)));
  }

  function focusSearchToggle() {
    const isShown = (btn) => btn && btn.offsetParent !== null;
    const target = isShown(lastSearchToggle) ? lastSearchToggle : searchToggles.find(isShown);
    if (target) target.focus();
  }

  function openSearch() {
    if (!searchOverlay) return;
    searchOverlay.classList.add('is-open');
    searchOverlay.setAttribute('aria-hidden', 'false');
    setSearchExpanded(true);
    // Mutex: opening search closes the menu panel if it's up.
    document.dispatchEvent(new CustomEvent('bnc:search:open'));
    // Focus after the next paint so the transition is allowed to start —
    // focusing mid-transition can suppress the screen-reader announce.
    if (searchInput) requestAnimationFrame(() => searchInput.focus());
  }

  function closeSearch() {
    if (!searchOverlay) return;
    searchOverlay.classList.remove('is-open');
    searchOverlay.setAttribute('aria-hidden', 'true');
    setSearchExpanded(false);
  }

  function isSearchOpen() {
    return searchOverlay && searchOverlay.classList.contains('is-open');
  }

  // ── Event wiring ────────────────────────────────────────────────────────────

  if (hamburger) {
    hamburger.addEventListener('click', () => {
      if (isMenuOpen()) {
        closeMenu();
      } else {
        openMenu();
      }
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (isMenuOpen()) {
        closeMenu();
        return;
      }
      const dd = openDropdown();
      if (dd) {
        dd.open = false;
        dd.querySelector('summary').focus();
        return;
      }
      if (isSearchOpen()) {
        closeSearch();
        focusSearchToggle();
      }
    }
    trapFocus(e);
  });

  searchToggles.forEach((btn) => {
    btn.addEventListener('click', () => {
      lastSearchToggle = btn;
      if (isSearchOpen()) {
        closeSearch();
      } else {
        openSearch();
      }
    });
  });

  // Dedicated close button
  if (searchClose) {
    searchClose.addEventListener('click', () => {
      closeSearch();
      focusSearchToggle();
    });
  }

  // Close search on outside click. Clicking inside the overlay (input, submit,
  // close), the toggle, OR the predictive-panel sibling counts as "inside the
  // search context" and should NOT close the overlay. Without the predictive-
  // panel exemption, clicking a predictive result (which lives in a sibling
  // panel injected by dopamiles-search.js) closed the overlay mid-click before
  // the result link could resolve.
  document.addEventListener('click', (e) => {
    if (!isSearchOpen()) return;
    if (searchOverlay.contains(e.target)) return;
    if (searchToggles.some((btn) => btn.contains(e.target))) return;
    const predictivePanel = document.getElementById('bnc-search-panel');
    if (predictivePanel && predictivePanel.contains(e.target)) return;
    closeSearch();
  });

  // Mutex listener: when search opens, close menu.
  document.addEventListener('bnc:search:open', () => {
    if (isMenuOpen()) closeMenu();
  });

  if (bagBtn) {
    bagBtn.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    });
  }

})();
